import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CameraPlusIcon as CameraPlus, ImageIcon as ImageIcon, TrashIcon as Trash, ArrowUpIcon as ArrowUp, ArrowDownIcon as ArrowDown, StorefrontIcon as Storefront, UserCircleIcon as UserCircle, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import { publicFileUrl, formatApiError } from "@/lib/utils-app";

/**
 * One card per provider profile that handles all the visual identity:
 *   - Profile photo (single, square-cropped hero shot)
 *   - Logo            (single, optional, wordmark/badge)
 *   - Portfolio gallery (up to 12 images with captions, reorderable)
 *
 * Upload pipeline mirrors the existing dog-photo flow: POST to
 * /api/upload returns a file_doc { id, storage_path, thumb_path, ... }.
 * We persist the `storage_path` on the provider record (NOT a full URL
 * — paths are stable across env moves and the file route serves them
 * publicly for approved providers; see routers/storage.py).
 *
 *   <ProviderMediaCard provider={provider} onRefresh={refresh} />
 */
const MAX_GALLERY = 12;
const ACCEPTED_TYPES = "image/jpeg,image/png,image/webp,image/gif";

async function uploadOne(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  // Backend returns { id, storage_path, thumb_path, ... }. We persist
  // the storage_path — that's the canonical reference used by
  // /api/files/{path} and is what dogs.photos / store hero_image use.
  const ref = res.data?.storage_path || res.data?.url;
  if (!ref) throw new Error("Upload did not return a file reference.");
  return ref;
}

export default function ProviderMediaCard({ provider, onRefresh }) {
  const [busy, setBusy] = useState(null); // "photo" | "logo" | "gallery"
  const [gallery, setGallery] = useState(() => Array.isArray(provider.gallery) ? provider.gallery : []);
  const photoInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // -----------------------------------------------------------------
  // Profile photo + logo (single-image fields)
  // -----------------------------------------------------------------
  const onSingleUpload = async (e, field, label) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(label);
    try {
      const url = await uploadOne(file);
      await api.patch("/providers/mine", { [field]: url });
      toast.success(`${label[0].toUpperCase() + label.slice(1)} updated.`);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
      e.target.value = ""; // reset so re-selecting same file works
    }
  };

  const clearSingle = async (field, label) => {
    if (!window.confirm(`Remove your ${label}?`)) return;
    setBusy(label);
    try {
      await api.patch("/providers/mine", { [field]: "" });
      toast.success(`${label[0].toUpperCase() + label.slice(1)} removed.`);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  // -----------------------------------------------------------------
  // Gallery — multi-image with captions + reordering
  // -----------------------------------------------------------------
  const persistGallery = async (next) => {
    try {
      await api.patch("/providers/mine", { gallery: next });
      setGallery(next);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (gallery.length + files.length > MAX_GALLERY) {
      toast.error(`Gallery limit is ${MAX_GALLERY} images. You can add ${MAX_GALLERY - gallery.length} more.`);
      e.target.value = "";
      return;
    }
    setBusy("gallery");
    try {
      const uploaded = await Promise.all(files.map(uploadOne));
      const next = [
        ...gallery,
        ...uploaded.map((url) => ({ url, caption: "" })),
      ];
      await persistGallery(next);
      toast.success(`Added ${uploaded.length} image${uploaded.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
      e.target.value = "";
    }
  };

  const moveGallery = (idx, dir) => {
    const next = [...gallery];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    persistGallery(next);
  };

  const removeGallery = (idx) => {
    if (!window.confirm("Remove this image from your gallery?")) return;
    const next = gallery.filter((_, i) => i !== idx);
    persistGallery(next);
  };

  const setCaption = (idx, caption) => {
    // Update local state immediately for snappy typing, debounced
    // persistence is handled by the blur handler below.
    setGallery((g) => g.map((item, i) => (i === idx ? { ...item, caption } : item)));
  };

  const onCaptionBlur = (idx) => {
    persistGallery(gallery.map((item, i) =>
      i === idx ? { ...item, caption: (item.caption || "").slice(0, 140) } : item
    ));
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  return (
    <div className="mb-8" data-testid="provider-media-card">
      <h2 className="font-spectral text-2xl tracking-tight mb-3 flex items-center gap-2">
        <Sparkle size={18} weight="duotone" className="text-gold" />
        Photos &amp; branding
      </h2>
      <p className="text-[13px] text-[#6B6B6B] mb-4 leading-relaxed">
        Profiles with a clear photo and a few portfolio shots book
        2&ndash;3x more often than text-only profiles. Add a profile
        photo (required), an optional logo for branding, and up to{" "}
        {MAX_GALLERY} portfolio images.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        {/* Profile photo */}
        <SinglePhotoSlot
          label="Profile photo"
          hint="Square crop. Headshot or your shop front works best."
          url={provider.photo_url}
          icon={UserCircle}
          inputRef={photoInputRef}
          busy={busy === "photo"}
          onPick={() => photoInputRef.current?.click()}
          onChange={(e) => onSingleUpload(e, "photo_url", "photo")}
          onClear={() => clearSingle("photo_url", "photo")}
          testid="provider-photo"
        />
        {/* Logo */}
        <SinglePhotoSlot
          label="Logo (optional)"
          hint="Transparent PNG or square wordmark. Shown next to your name."
          url={provider.logo_url}
          icon={Storefront}
          inputRef={logoInputRef}
          busy={busy === "logo"}
          onPick={() => logoInputRef.current?.click()}
          onChange={(e) => onSingleUpload(e, "logo_url", "logo")}
          onClear={() => clearSingle("logo_url", "logo")}
          testid="provider-logo"
        />
      </div>

      {/* Portfolio gallery -------------------------------------------- */}
      <div className="bg-white border border-[#EAEAEA] rounded-xl p-4" data-testid="provider-gallery">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-spectral text-lg tracking-tight">Portfolio gallery</h3>
            <p className="text-[12px] text-[#6B6B6B]">
              {gallery.length} / {MAX_GALLERY} images. Drag-free reorder with the arrows.
            </p>
          </div>
          <Button
            size="sm"
            disabled={busy === "gallery" || gallery.length >= MAX_GALLERY}
            onClick={() => galleryInputRef.current?.click()}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="provider-gallery-add"
          >
            <CameraPlus size={13} weight="bold" className="mr-1.5" />
            {busy === "gallery" ? "Uploading…" : "Add photos"}
          </Button>
          <input
            ref={galleryInputRef}
            type="file"
            multiple
            accept={ACCEPTED_TYPES}
            onChange={onGalleryUpload}
            className="hidden"
            data-testid="provider-gallery-input"
          />
        </div>

        {gallery.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-[#E2DAC2] bg-cream/30 px-4 py-10 text-center" data-testid="provider-gallery-empty">
            <ImageIcon size={28} weight="duotone" className="text-[#9A9A9A] mx-auto mb-2" />
            <p className="text-[13px] text-[#6B6B6B] leading-relaxed">
              No gallery photos yet. Add before/after grooms, training-in-action shots,
              your van/rig, or smiling-client-dog photos to build trust.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {gallery.map((item, idx) => (
              <div
                key={`${item.url}-${idx}`}
                className="relative rounded-lg overflow-hidden border border-[#EAEAEA] bg-white"
                data-testid={`provider-gallery-item-${idx}`}
              >
                <img
                  src={publicFileUrl(item.url)}
                  alt={item.caption || `Gallery image ${idx + 1}`}
                  className="w-full h-32 object-cover"
                  loading="lazy"
                />
                <div className="p-2 space-y-2">
                  <Input
                    placeholder="Caption (optional, 140 char max)"
                    value={item.caption || ""}
                    onChange={(e) => setCaption(idx, e.target.value.slice(0, 140))}
                    onBlur={() => onCaptionBlur(idx)}
                    className="h-7 text-[11.5px] px-2"
                    data-testid={`provider-gallery-caption-${idx}`}
                  />
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => moveGallery(idx, -1)}
                        disabled={idx === 0}
                        className="p-1.5 rounded text-[#6B6B6B] hover:text-ink hover:bg-cream/50 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Move left"
                        data-testid={`provider-gallery-up-${idx}`}
                      >
                        <ArrowUp size={11} weight="bold" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveGallery(idx, 1)}
                        disabled={idx === gallery.length - 1}
                        className="p-1.5 rounded text-[#6B6B6B] hover:text-ink hover:bg-cream/50 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Move right"
                        data-testid={`provider-gallery-down-${idx}`}
                      >
                        <ArrowDown size={11} weight="bold" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeGallery(idx)}
                      className="p-1.5 rounded text-crimson hover:bg-crimson/10"
                      aria-label="Remove image"
                      data-testid={`provider-gallery-remove-${idx}`}
                    >
                      <Trash size={11} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SinglePhotoSlot({ label, hint, url, icon: Icon, inputRef, busy, onPick, onChange, onClear, testid }) {
  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl p-4 flex gap-3 items-start"
      data-testid={testid}
    >
      <div className="flex-shrink-0">
        {url ? (
          <img
            src={publicFileUrl(url)}
            alt={label}
            className="w-20 h-20 object-cover rounded-lg border border-[#EAEAEA] bg-cream"
            data-testid={`${testid}-preview`}
          />
        ) : (
          <div className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E2DAC2] bg-cream/40 flex items-center justify-center">
            <Icon size={28} weight="duotone" className="text-[#9A9A9A]" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-spectral text-[15px] text-ink leading-tight">{label}</p>
        <p className="text-[11.5px] text-[#6B6B6B] mt-0.5 leading-snug">{hint}</p>
        <div className="flex gap-2 mt-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onPick}
            className="h-7 text-[11.5px] rounded-full px-3"
            data-testid={`${testid}-upload`}
          >
            <CameraPlus size={11} weight="bold" className="mr-1" />
            {busy ? "Uploading…" : url ? "Replace" : "Upload"}
          </Button>
          {url && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={onClear}
              className="h-7 text-[11.5px] text-crimson hover:bg-crimson/10 rounded-full px-3"
              data-testid={`${testid}-clear`}
            >
              <Trash size={11} weight="bold" className="mr-1" />
              Remove
            </Button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={onChange}
            className="hidden"
            data-testid={`${testid}-input`}
          />
        </div>
      </div>
    </div>
  );
}
